import React, { useEffect, useState, ChangeEvent } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Common, EmployeeIcons } from "../../assets/images";
import { LoaderBlue } from "../Lotties";

interface Props {
  bgImage: boolean;
  setBgImage: any;
  imagePreview: string;
  setImagePreview: any;
  imageBGPreview: string;
  setImageBGPreview: any;
}

const CustomImageUpload: React.FC<Props> = ({
  bgImage,
  setBgImage,
  imageBGPreview,
  imagePreview,
  setImageBGPreview,
  setImagePreview,
}) => {
  const [imageUploaded, setImageUploaded] = useState<boolean>(false);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [fileNameWithExtension, setFileNameWithExtension] =
    useState<string>("");
  // BGImage state
  const [imageBGUploaded, setImageBGUploaded] = useState<boolean>(false);
  const [imageBGLoading, setImageBGLoading] = useState<boolean>(false);
  const [bgTitle, setBgTitle] = useState<string>("");
  const [bgFileNameWithExtension, setBgFileNameWithExtension] =
    useState<string>("");

  useEffect(() => {
    const savedImage = sessionStorage.getItem("uploadedImage");
    const savedTitle = sessionStorage.getItem("uploadedImageTitle");
    const savedExtension = sessionStorage.getItem("uploadedImageExtension");

    if (savedImage) {
      setImagePreview(savedImage);
      setImageUploaded(true);
      setTitle(savedTitle || "uploaded-file");
      setFileNameWithExtension(savedExtension || "jpg");
    }
  }, []);

  const handleUploadImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageLoading(true);
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64Image = reader.result as string;
        const fileName = file.name.split(".").slice(0, -1).join(".");
        const extension = file.name.split(".").pop() || "";

        // Save to sessionStorage
        sessionStorage.setItem("uploadedImage", base64Image);
        sessionStorage.setItem("uploadedImageTitle", fileName);
        sessionStorage.setItem("uploadedImageExtension", extension);

        // Update state
        setImagePreview(base64Image);
        setTitle(fileName);
        setFileNameWithExtension(extension);
        setImageUploaded(true);
        setImageLoading(false);
      };

      reader.readAsDataURL(file);
    }
  };

  const handleUploadBGImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageBGLoading(true);
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64Image = reader.result as string;
        const fileName = file.name.split(".").slice(0, -1).join(".");
        const extension = file.name.split(".").pop() || "";

        // Save to sessionStorage
        sessionStorage.setItem("uploadedBGImage", base64Image);
        sessionStorage.setItem("uploadedBGImageTitle", fileName);
        sessionStorage.setItem("uploadedBGImageExtension", extension);

        // Update state
        setImageBGPreview(base64Image);
        setBgTitle(fileName);
        setBgFileNameWithExtension(extension);
        setImageBGUploaded(true);
        setImageBGLoading(false);
      };

      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setImagePreview("");
    setImageUploaded(false);
    setTitle("");
    setFileNameWithExtension("");
    sessionStorage.removeItem("uploadedImage");
    sessionStorage.removeItem("uploadedImageTitle");
    sessionStorage.removeItem("uploadedImageExtension");
  };

  const handleRemoveBGLogo = () => {
    setImageBGPreview("");
    setImageBGUploaded(false);
    setBgTitle("");
    setBgFileNameWithExtension("");
    sessionStorage.removeItem("uploadedBGImage");
    sessionStorage.removeItem("uploadedBGImageTitle");
    sessionStorage.removeItem("uploadedBGImageExtension");
  };

  return (
    <div className="mb-5">
      <Row className="align-items-end text-start">
        <Col sm="12">
          <Row>
            <Col sm="12">
              <Form.Group>
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "18px" }}
                >
                  Upload Custom Logo
                </h4>

                {imageLoading ? (
                  <LoaderBlue height={50} width={50} />
                ) : (
                  <Form.Label
                    className="profile-image-label cursor-pointer d-flex align-items-center justify-content-center"
                    style={{ height: "auto", padding: "8px 24px" }}
                  >
                    <Form.Control
                      type="file"
                      name="profileImage"
                      onChange={handleUploadImage}
                      accept=".svg"
                      className="d-none"
                      multiple={false}
                    />
                    <div className="text-center d-flex align-items-center gap-3">
                      <div
                        className="add-profile-image d-flex align-items-center justify-content-center mx-auto"
                        style={{ width: "30px", height: "30px" }}
                      >
                        <img src={EmployeeIcons.Plus} width="25px" alt="plus" />
                      </div>
                      <p
                        className="upload-para mb-0"
                        style={{ fontSize: "14px" }}
                      >
                        Upload SVG File only
                      </p>
                    </div>
                  </Form.Label>
                )}
                {imageUploaded && (
                  <div className="d-flex mt-3 align-items-center">
                    <div className="text-center">
                      <img
                        src={
                          ["jpg", "png", "jpeg"].includes(
                            fileNameWithExtension.toLowerCase(),
                          )
                            ? imagePreview || Common.ImageIcon
                            : Common.FileIcon
                        }
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                        alt="uploaded"
                      />
                      <p className="para-medium textBlack text-center">
                        {title}.{fileNameWithExtension}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveLogo()}
                      className="btn primary-button"
                      type="button"
                      style={{
                        height: "fit-content",
                        padding: "0px 6px",
                        marginLeft: "10px",
                      }}
                    >
                      <img
                        src={Common.Cross}
                        height="14px"
                        width="14px"
                        alt="cross"
                      />
                    </button>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col sm="6">
              <Form.Group className="my-4">
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "18px" }}
                >
                  Background Image
                </h4>
                <Form.Check
                  type="checkbox"
                  id="bgImageCheckbox" // Add an id to target the checkbox
                  label="Add Background Image"
                  className="hide-letter-head"
                  name="bgImage"
                  checked={bgImage}
                  onChange={(e) => {
                    setBgImage(e.target.checked);
                  }}
                />
              </Form.Group>
            </Col>
            {bgImage && (
              <Col sm="12">
                <Form.Group>
                  <h4
                    className="sub-heading normal-font-weight textBlack"
                    style={{ fontSize: "18px" }}
                  >
                    Upload Letterhead Background Image
                  </h4>

                  {imageBGLoading ? (
                    <LoaderBlue height={50} width={50} />
                  ) : (
                    <Form.Label
                      className="profile-image-label cursor-pointer d-flex align-items-center justify-content-center"
                      style={{ height: "auto", padding: "8px 24px" }}
                    >
                      <Form.Control
                        type="file"
                        name="profileImage"
                        onChange={handleUploadBGImage}
                        accept=".png"
                        className="d-none"
                        multiple={false}
                      />
                      <div className="text-center d-flex align-items-center gap-3">
                        <div
                          className="add-profile-image d-flex align-items-center justify-content-center mx-auto"
                          style={{ width: "30px", height: "30px" }}
                        >
                          <img
                            src={EmployeeIcons.Plus}
                            width="25px"
                            alt="plus"
                          />
                        </div>
                        <p
                          className="upload-para mb-0"
                          style={{ fontSize: "14px" }}
                        >
                          Upload PNG File only
                        </p>
                      </div>
                    </Form.Label>
                  )}
                  {imageBGUploaded && (
                    <div className="d-flex mt-3 align-items-center">
                      <div className="text-center">
                        <img
                          src={
                            ["jpg", "png", "jpeg"].includes(
                              bgFileNameWithExtension.toLowerCase(),
                            )
                              ? imageBGPreview || Common.ImageIcon
                              : Common.FileIcon
                          }
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "contain",
                          }}
                          alt="uploaded"
                        />
                        <p className="para-medium textBlack text-center">
                          {bgTitle}.{bgFileNameWithExtension}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveBGLogo()}
                        className="btn primary-button"
                        type="button"
                        style={{
                          height: "fit-content",
                          padding: "0px 6px",
                          marginLeft: "10px",
                        }}
                      >
                        <img
                          src={Common.Cross}
                          height="14px"
                          width="14px"
                          alt="cross"
                        />
                      </button>
                    </div>
                  )}
                </Form.Group>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default CustomImageUpload;
